import * as S from './TitleSection.styles'
import useScreenSize from '../../../../hooks/useScreenSize'
import PremiumFrame from './PremiumFrame/PremiumFrame'
import { getFrameProps } from './TitleSection.utils'
import { useTranslation } from 'react-i18next'
import { useMemo, useRef } from 'react'
import { useNoSSR } from 'utils/NoSSR'

type Props = {
  artworkUrls: string[]
  categoryName: string | null
}

const TITLE_TEXT_HEIGHT = 200
const TILE_ASPECT_RATIO = 0.75

export default function TitleSection({ artworkUrls, categoryName }: Props) {
  const isMediumScreen = useScreenSize('md')
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const isClient = useNoSSR()
  const artworkHeight = useMemo(() => {
    if (!isClient) {
      return isMediumScreen ? 320 : 160
    }
    const containerHeight = containerRef.current?.clientHeight || 0
    return Math.floor((containerHeight - TITLE_TEXT_HEIGHT) / 2)
  }, [isClient, containerRef, isMediumScreen])

  const title = categoryName
    ? t('art.home.title_with_category', { v1: categoryName })
    : t('art.home.title')

  return (
    <S.Container ref={containerRef}>
      <S.TitleText>{title}</S.TitleText>
      <S.SubtitleText>{t('art.home.subtitle')}</S.SubtitleText>
      <S.ArtworksContainer>
        <S.SliderRow speed={20} gradient={false} direction="left" autoFill>
          {artworkUrls
            .filter((_, index) => index % 2 === 0)
            .map((url, index) => (
              <S.ArtworkContainer
                key={url}
                $height={artworkHeight}
                $width={TILE_ASPECT_RATIO * artworkHeight}
              >
                <PremiumFrame imageUrl={url} {...getFrameProps(index, 'top')} />
              </S.ArtworkContainer>
            ))}
        </S.SliderRow>
        <S.SliderRow speed={20} gradient={false} direction="right" autoFill>
          {artworkUrls
            .filter((_, index) => index % 2 === 1)
            .map((url, index) => (
              <S.ArtworkContainer
                key={url}
                $height={artworkHeight}
                $width={TILE_ASPECT_RATIO * artworkHeight}
              >
                <PremiumFrame
                  imageUrl={url}
                  {...getFrameProps(index, 'bottom')}
                />
              </S.ArtworkContainer>
            ))}
        </S.SliderRow>
        {isMediumScreen && <S.GradientContainer />}
      </S.ArtworksContainer>
    </S.Container>
  )
}
